/**
 * Version Check & Cache Invalidation Service
 * 
 * Automatically detects new deployments and prompts users to refresh.
 * Clears old caches when version mismatch is detected.
 */

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_STORAGE_KEY = 'app_version';
const BUILD_TIME_STORAGE_KEY = 'app_build_time';

class VersionService {
  constructor() {
    this.currentVersion = null;
    this.checkInterval = null;
    this.onUpdateAvailable = null;
  }

  /**
   * Initialize version checking
   * @param {Function} onUpdateAvailable - Callback when new version is detected
   */
  init(onUpdateAvailable) {
    this.onUpdateAvailable = onUpdateAvailable;
    this.checkVersion(true); // Initial check
    this.startPeriodicCheck();
    
    // Also check when tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkVersion();
      }
    });
  }

  /**
   * Start periodic version checks
   */
  startPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    this.checkInterval = setInterval(() => {
      this.checkVersion();
    }, VERSION_CHECK_INTERVAL);
  }

  /**
   * Check for new version
   * @param {boolean} isInitial - Is this the initial check on app load
   */
  async checkVersion(isInitial = false) {
    try {
      // Add cache-busting query param
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        console.warn('Version check failed:', response.status);
        return;
      }

      const serverVersion = await response.json();
      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      const storedBuildTime = localStorage.getItem(BUILD_TIME_STORAGE_KEY);

      // Store current version info
      this.currentVersion = serverVersion;

      if (isInitial) {
        // First load - just store the version
        localStorage.setItem(VERSION_STORAGE_KEY, serverVersion.version);
        localStorage.setItem(BUILD_TIME_STORAGE_KEY, serverVersion.buildTime);
        console.log(`App version: ${serverVersion.version}`);
        return;
      }

      // Check if version has changed
      if (storedVersion && storedBuildTime) {
        const versionChanged = storedVersion !== serverVersion.version;
        const buildTimeChanged = storedBuildTime !== serverVersion.buildTime;

        if (versionChanged || buildTimeChanged) {
          console.log(`New version detected: ${storedVersion} -> ${serverVersion.version}`);
          
          // Update stored version
          localStorage.setItem(VERSION_STORAGE_KEY, serverVersion.version);
          localStorage.setItem(BUILD_TIME_STORAGE_KEY, serverVersion.buildTime);
          
          // Notify about update
          if (this.onUpdateAvailable) {
            this.onUpdateAvailable(serverVersion);
          }
        }
      }
    } catch (error) {
      console.warn('Version check error:', error);
    }
  }

  /**
   * Clear all caches and reload
   */
  async clearCacheAndReload() {
    try {
      // Clear service worker caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('Cleared service worker caches');
      }

      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
        console.log('Unregistered service workers');
      }

      // Clear session storage
      sessionStorage.clear();

      // Hard reload (bypass cache)
      window.location.reload(true);
    } catch (error) {
      console.error('Error clearing cache:', error);
      // Fallback to normal reload
      window.location.reload();
    }
  }

  /**
   * Get current version info
   */
  getVersion() {
    return this.currentVersion || {
      version: localStorage.getItem(VERSION_STORAGE_KEY) || 'unknown',
      buildTime: localStorage.getItem(BUILD_TIME_STORAGE_KEY) || 'unknown'
    };
  }

  /**
   * Force version check now
   */
  forceCheck() {
    this.checkVersion();
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }
}

// Export singleton instance
export const versionService = new VersionService();

// Export helper function for manual cache clear
export const clearCacheAndReload = () => versionService.clearCacheAndReload();
